import { setQuery, toggleNetwork } from '@/features/filterSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useUserClientContext } from '@/app/clientContext';
import {
  DISTRIBUTION_CLIENT,
  MULTI_CLIENT,
  TRANSMISSION_CLIENT,
} from '@/features/types';
import { MultiNetworkFilterButtons } from './MultiNetworkFilterButtons';
import { FilterSelect } from '@/components';
import classes from './FilterButtonsNetworkGroups.module.css';
import type { RootState } from '@/app/store';
import type { FilterOption } from '@/components/FilterSelect/FilterSelect';
import {
  useNetworkGroupLabel,
  getFilterableGroups,
  hasNetworkGroupType,
} from '@/helpers/networkGroups';
import { useFetchNetworkGroupsWithValuesQuery } from '@/features/networkGroupSlice';
import { NetworkGroupType } from '@/generated/types/navigator/types.gen';

type GroupIds = 1 | 2 | 3 | 4 | 5 | 6;

export const FilterButtonsNetworkGroups = () => {
  const dispatch = useAppDispatch();
  const { context } = useUserClientContext();
  const query = useAppSelector((state) => state.filterQuery);
  const { activeGroupName } = useAppSelector(
    (state: RootState) => state.analysisMap
  );
  const { data: networkGroups } = useFetchNetworkGroupsWithValuesQuery();
  const planningGroupData = networkGroups?.find((group) =>
    hasNetworkGroupType(group, NetworkGroupType.PLANNING)
  );
  const planningGroup = planningGroupData?.id ?? null;
  const networkGroupLabel = useNetworkGroupLabel(planningGroupData);

  // Check if the planning group should be shown based on category
  const shouldShowPlanningGroup = () => {
    if (!planningGroup || !networkGroups) return false;

    const filterableGroups = getFilterableGroups(networkGroups);
    return filterableGroups.some((group) => group.id === planningGroup);
  };

  const selectedGroup = query.groups[planningGroup as GroupIds]?.filter(
    (v) => v !== null
  );
  return (
    <div className={classes.container}>
      {[TRANSMISSION_CLIENT, DISTRIBUTION_CLIENT].includes(
        context.clientType
      ) &&
        planningGroupData &&
        planningGroup &&
        !activeGroupName &&
        shouldShowPlanningGroup() && (
          <FilterSelect
            data-testid="filter-select-planning-group"
            label={networkGroupLabel ?? ''}
            placeholder={networkGroupLabel ?? ''}
            selected={selectedGroup ?? []}
            options={(planningGroupData.values ?? []).map((v) => ({
              label: v,
              value: v,
            }))}
            onSubmit={(s: FilterOption[]) => {
              dispatch(
                setQuery({
                  ...query,
                  groups: {
                    ...query.groups,
                    [planningGroup as GroupIds]: s.map(
                      (c: FilterOption) => c.value
                    ),
                  },
                })
              );
            }}
            isActive={!!selectedGroup?.length}
          />
        )}
      {context.clientType === MULTI_CLIENT && (
        <MultiNetworkFilterButtons
          network={query.network}
          updateNetwork={(network: string) =>
            dispatch(toggleNetwork({ network }))
          }
        />
      )}
    </div>
  );
};
